import * as Mousetrap from 'mousetrap';
import utils from '../utils';
import { toHTML } from '../uTheory.Font/font';

/**
 * @param {HTMLButton} button
 * @param {Function} callback
 * @param {string|string[]} hotKey
 * @returns button;
 */
const addCallback = (button, callback, hotKey?: string | string[]) => {
  if (!utils.isFunction(callback)) throw new Error('Callback must be a function.');
  button.addEventListener('click', callback);
  if (hotKey) {
    Mousetrap.bind(hotKey, callback);
  }
  return button;
};

interface ToggleButton {
  button: HTMLButtonElement;
  textSpan: HTMLSpanElement;
  toggle: (n?: number | false, shadow?: boolean) => void;
  readonly value: any;
}

/**
 * @param {Object} options
 * @param {string[]} options.texts
 * @param {string[]} options.value
 * @param {string} options.className
 * @param {string[]|boolean} options.hotKey
 * @param {Function} options.callback
 * @returns {Object} ButtonObj {
 *    button: HTMLButtonElement,
 *    setRepetitions,
 *    toggle,
 * }
 */
const makeToggleButton = ({
  texts,
  values,
  className,
  hotKey,
  callback,
  shiftStyling = false,
}: {
  texts: string[];
  values: any[];
  className?: string;
  hotKey?: string | string[];
  callback: Function;
  shiftStyling?: boolean;
}): ToggleButton => {
  const button = document.createElement('button');
  button.classList.add('uButton', 'uLetterPanelButton');
  if (className) button.classList.add(className);
  const textSpan = document.createElement('span');

  button.appendChild(textSpan);
  textSpan.innerHTML = texts[0];

  if (shiftStyling) {
    textSpan.style.display = 'inline-block';
    textSpan.style.webkitTextStrokeWidth = '0.3em';
    textSpan.style.webkitTextStroke = '0.3em';
    (textSpan.style as any).textStrokeWidth = '0.3em';
    (textSpan.style as any).textStroke = '0.3em';
    textSpan.style.transform = 'scale(0.5, 0.7)';
  }

  let toggleCount = 0;
  const toggleShadow = () => {
    if (toggleCount % texts.length) {
      button.style.filter = 'lighten(1.1)';
      button.style.webkitFilter = 'lighten(1.1)';
      textSpan.style.filter = 'invert(100%)';
      textSpan.style.webkitFilter = 'invert(100%)';
    } else {
      button.style.filter = '';
      button.style.webkitFilter = '';
      textSpan.style.filter = '';
      textSpan.style.webkitFilter = '';
    }
  };

  const toggle = (n: number | false = false, shadow = false) => {
    if (n !== false) toggleCount = n;
    else toggleCount++;
    textSpan.innerHTML = texts[toggleCount % texts.length];
    if (shadow) toggleShadow();
  };

  const value = () => values[toggleCount % values.length];

  const cb = () => {
    callback(value());
  };

  addCallback(button, cb, hotKey);

  return {
    button,
    textSpan,
    toggle,
    get value() {
      return value();
    },
  };
};

const upArrow = '&#x21E7;';
// const upArrow = '&#8682;';
// const downArrow = '&#x21E9;';

interface RomanNumeralButtonsOptions {
  diminishedButtons: boolean;
  chordTypes: 'triads' | 'sevenths' | 'both';
}

/**
 * @param {domElement} el the dom element we add buttons to
 * @param {LetterPanel} letterPanel
 * @param {Object} options
 * @param {boolean} options.diminishedButtons default true
 * @param {string} options.chordTypes 'triads', 'sevenths', 'both'*
 */
export default class RomanNumeralButtons {
  options: RomanNumeralButtonsOptions;

  callbacks: Function[];

  el: HTMLDivElement;

  shift: ToggleButton;

  romanButtons: ToggleButton[];

  halfDim: ToggleButton;

  dim: ToggleButton;

  constructor(el, letterPanel, options: Partial<RomanNumeralButtonsOptions> = {}) {
    this.options = utils.merge(options, {
      diminishedButtons: true,
      chordTypes: 'both',
    });

    this.callbacks = [];
    this.el = el;
    this.init(letterPanel);
  }

  init(letterPanel) {
    const el = this.el;

    this.shift = makeToggleButton({
      texts: [upArrow, upArrow],
      values: ['upper', 'lower'],
      callback: this.shiftLetters.bind(this),
      shiftStyling: true,
    });

    const i = toHTML('romanNumeral', 'i');
    const v = toHTML('romanNumeral', 'v');
    const I = toHTML('romanNumeral', 'I');
    const V = toHTML('romanNumeral', 'V');

    this.romanButtons = [
      { texts: [i, I], values: ['i', 'I'] },
      { texts: [v, V], values: ['v', 'V'] },
    ].map(({ texts, values }) =>
      makeToggleButton({
        texts,
        values,
        className: 'accidentals',
        hotKey: values,
        callback: this.updateRN.bind(this),
      })
    );

    Mousetrap.bind(
      'shift',
      () => {
        this.shift.toggle(1, true);
        this.romanButtons.forEach((b) => b.toggle(1));
      },
      'keydown'
    );

    Mousetrap.bind(
      'shift',
      () => {
        this.shift.toggle(0, true);
        this.romanButtons.forEach((b) => b.toggle(0));
      },
      'keyup'
    );

    el.appendChild(this.shift.button);
    this.romanButtons.map((o) => o.button).forEach(el.appendChild.bind(el));

    if (this.options.diminishedButtons && this.options.chordTypes !== 'triads') {
      this.halfDim = makeToggleButton({
        texts: [toHTML('quality', 'o/')],
        values: ['o/'],
        hotKey: ['h', 'H', '/'],
        callback: this.updateRN.bind(this),
      });
      el.appendChild(this.halfDim.button);
    }

    if (this.options.diminishedButtons && this.options.chordTypes !== 'sevenths') {
      this.dim = makeToggleButton({
        texts: [toHTML('quality', 'o')],
        values: ['o'],
        hotKey: ['o', 'O'],
        callback: this.updateRN.bind(this),
      });
      el.appendChild(this.dim.button);
    }

    // Create a br if this is not the last element
    letterPanel.createCustomButton({
      tagName: 'br',
      class: 'uHideLastChild',
    });
  }

  shiftLetters(value) {
    console.log(`shifting ${value}`);
    if (value === 'upper') {
      this.romanButtons.forEach((b) => b.toggle(1));
    } else {
      this.romanButtons.forEach((b) => b.toggle(0));
    }
    this.shift.toggle(undefined, true);
  }

  updateRN(value) {
    this.callbacks.forEach((cb) => cb(value));
  }

  /**
   * @param {Function} cb
   * @param {Object} thisArg
   */
  addCallback(cb, thisArg = false) {
    cb = thisArg ? cb.bind(thisArg) : cb;
    this.callbacks.push(cb);
  }

  clearCallbacks() {
    this.callbacks.length = 0;
  }
}
